export enum UserRole {
  ARTIST = 'ARTIST',
  MANAGER = 'MANAGER',
}

export enum ArtworkStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  PUBLISHED = 'PUBLISHED',
}

export enum OrderStatus {
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  FULFILLED = 'FULFILLED',
}

export enum TaskType {
  PUBLISH_ARTWORK = 'PUBLISH_ARTWORK',
  UNPUBLISH_ARTWORK = 'UNPUBLISH_ARTWORK',
  ANALYZE_ARTWORK = 'ANALYZE_ARTWORK',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  artisticName?: string
  bio?: string
  paypal?: string
  role: UserRole
  signature: string
  verified: boolean
  shopifyCollectionId?: string
  shopifyCollectionHandle?: string
  picture?: boolean
  dateCreate?: string
  dateLastLogin?: string
  createdAt?: string
  updatedAt?: string
}

export interface ArtistDetail {
  id: number
  email: string
  paypalEmail: string
  firstName: string
  lastName: string
  artistName: string
  shopifyHandle: string
  hasPicture: boolean
  verified: boolean
  dateCreate: string
  dateLastLogin: string
  artworksDraftCount: number
  artworksPendingCount: number
  artworksPublishedCount: number
}

export interface Tag {
  id: number
  name: string
}

export interface Artwork {
  id: number
  artistId: number
  name: string
  description: string
  status: ArtworkStatus
  allowWholesale: boolean
  matureContent: boolean
  // The API sends the artwork's tags as objects; a save sends back their ids.
  tags: Tag[]
  published?: string
  createdAt: string
  updatedAt: string
  fileName?: string
  /** A manager has held this pending artwork: it needs the artist's attention. */
  reviewHold?: boolean | null
  /** The manager's note explaining the hold, when there is one the artist may see. */
  reviewNote?: string | null
  /** Dev-only: Firestore console URL for this artwork */
  _firestoreConsoleUrl?: string
}

export interface Size {
  id: number
  name: string
  width: number
  height: number
  order: number
}

export interface FrameType {
  id: number
  name: string
  description: string
}

// One size + product type of an artwork (db/models/commission.js). `commission` is a fixed
// amount in GBP that the artist earns per sale, not a percentage, and it only applies when
// `isCustom` is set; otherwise the artist earns `defaultCommission`.
export interface Commission {
  artworkId: number
  sizeId: number
  frameTypeId: number
  commission: number
  defaultCommission: number
  isCustom: boolean
  basePrice: number | null
  vat: number
}

export interface Sale {
  id: string
  vendorName: string
  vendorEmail: string
  orderId: number
  orderStatus: OrderStatus
  orderClosedDate: string
  artworkId: number
  artworkName: string
  productSku: string
  productQuantity: number
  productPrice: number
  commission: number
}

export interface Task {
  id: string
  type: TaskType
  status: TaskStatus
  artworkId: number | null
  artworkName?: string
  cloudTaskName?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  errorMessage?: string
  retryCount: number
  maxRetries: number
  metadata: Record<string, unknown>
  steps?: TaskStep[]
}

export interface TaskStep {
  id: string
  taskId: string
  stepName: string
  stepLabel: string
  stepOrder: number
  status: TaskStatus
  startedAt?: string
  completedAt?: string
  details: Record<string, unknown>
  errorMessage?: string
}

export enum ArtifactType {
  PRODUCT_IMAGE = 'PRODUCT_IMAGE',
  PREVIEW_IMAGE = 'PREVIEW_IMAGE',
  EXPORT_FILE = 'EXPORT_FILE',
}

export interface TaskArtifact {
  id: string
  taskId: string
  stepId?: string
  artifactType: ArtifactType
  name: string
  gcsPath: string
  publicUrl?: string  // Contains GCS console URL (no public access with uniform bucket ACL)
  fileSize?: number
  mimeType?: string
  metadata: Record<string, unknown>
  createdAt: string
}

// ─── Support Types ──────────────────────────────────────

export type TicketStatus =
  | 'new'
  | 'agent_processing'
  | 'awaiting_customer'
  | 'awaiting_operator'
  | 'operator_replied'
  | 'resolved'
  | 'closed'
  | 'spam'

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent'

export type TicketChannel = 'email' | 'admin' | 'api'

export type MessageDirection = 'inbound' | 'outbound'

export type MessageSenderType = 'customer' | 'agent' | 'operator'

export type TicketIntent =
  | 'order_status'
  | 'fulfillment_tracking'
  | 'artwork_inquiry'
  | 'return_exchange'
  | 'artist_inquiry'
  | 'general'
  | 'spam'

export type TicketSentiment = 'positive' | 'neutral' | 'negative' | 'urgent'

export interface SupportContact {
  id: string
  email: string
  name: string | null
  userId: number | null
  firstSeenAt: string
  lastSeenAt: string
  ticketCount: number
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface SupportTicket {
  id: string
  contactId: string
  resendThreadKey: string | null
  subject: string
  status: TicketStatus
  priority: TicketPriority
  intent: TicketIntent | null
  confidence: number | null
  sentiment: TicketSentiment | null
  tags: string[]
  assignedTo: number | null
  resolvedBy: string | null
  channel: TicketChannel
  shopifyOrderId: string | null
  artworkId: number | null
  artistId: number | null
  createdAt: string
  updatedAt: string
  firstResponseAt: string | null
  resolvedAt: string | null
  closedAt: string | null
  slaBreached: boolean
  metadata: Record<string, unknown>
  // Joined fields
  contactEmail?: string
  contactName?: string
  messageCount?: number
}

export interface SupportMessage {
  id: string
  ticketId: string
  resendEmailId: string | null
  resendMessageId: string | null
  direction: MessageDirection
  senderType: MessageSenderType
  fromAddress: string
  toAddress: string | null
  subject: string | null
  bodyText: string | null
  bodyHtml: string | null
  deliveredAt: string | null
  bouncedAt: string | null
  bounceReason: string | null
  hasAttachments: boolean
  metadata: Record<string, unknown>
  createdAt: string
}

export interface SupportAgentAction {
  id: string
  ticketId: string
  messageId: string | null
  actionType: string
  toolName: string | null
  toolInput: Record<string, unknown> | null
  toolOutput: Record<string, unknown> | null
  modelUsed: string | null
  promptTokens: number | null
  completionTokens: number | null
  latencyMs: number | null
  success: boolean
  errorMessage: string | null
  createdAt: string
}

export interface SupportCannedResponse {
  id: string
  title: string
  body: string
  category: string
  variables: string[]
  isActive: boolean
  usageCount: number
  createdBy: number | null
  createdAt: string
  updatedAt: string
}

export interface SupportAgentConfig {
  id: string
  key: string
  value: unknown
  description: string | null
  updatedAt: string
  updatedBy: number | null
}

export interface SupportStats {
  totalTickets: number
  newTickets: number
  awaitingOperator: number
  awaitingCustomer: number
  resolvedTickets: number
  avgFirstResponseMinutes: number | null
  slaBreachedCount: number
  agentAutoReplied: number
  agentEscalated: number
}
